/*
 * <stockist-finder>: search, type filters, "use my location" and a Leaflet map
 * for the main-stockists section.
 *
 * The list is rendered in Liquid and works without JS. This adds filtering
 * and the map. Leaflet is loaded from cdnjs only when the map is about to
 * scroll into view, so it costs nothing on first paint.
 */
if (!customElements.get('stockist-finder')) {
  const LEAFLET = {
    css: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css',
    cssIntegrity: 'sha384-sHL9NAb7lN7rfvG5lfHpm643Xkcjzp4jFvuavGOndn6pjVqS6ny56CAt3nsEVT4H',
    js: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js',
    jsIntegrity: 'sha384-NElt3Op+9NBMCYaef5HxeJmU4Xeard/Lku8ek6hoPTvYkQPh3zLIrJP7KiRocsxO',
  };

  // Groups nearby pins into count badges. Its two CSS rules live in section-main-stockists.css.
  const MARKER_CLUSTER = {
    js: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.js',
    jsIntegrity:
      'sha512-OFs3W4DIZ5ZkrDhBFtsCP6JXtMEDGmhl0QPlmWYBJay40TT1n3gt2Xuw8Pf/iezgW9CdabjkNChRqozl/YADmg==',
  };

  // Whole of the UK, used when there's nothing to fit the map to
  const UK_VIEW = { center: [54.6, -3.4], zoom: 5 };
  const CLOSE_ZOOM = 13;
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  let leafletPromise;

  function loadScript(src, integrity) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.integrity = integrity;
      script.crossOrigin = 'anonymous';
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  function loadLeaflet() {
    if (window.L?.markerClusterGroup) return Promise.resolve(window.L);
    if (!leafletPromise) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET.css;
      link.integrity = LEAFLET.cssIntegrity;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);

      // The cluster plugin extends L, so it has to load after Leaflet
      leafletPromise = (window.L ? Promise.resolve() : loadScript(LEAFLET.js, LEAFLET.jsIntegrity))
        .then(() => loadScript(MARKER_CLUSTER.js, MARKER_CLUSTER.jsIntegrity))
        .then(() => window.L)
        .catch((error) => {
          leafletPromise = null;
          throw error;
        });
    }
    return leafletPromise;
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }

  function normalise(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function milesBetween(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(h));
  }

  function formatMiles(miles, suffix = 'away') {
    if (miles < 0.1) return `under 0.1 miles ${suffix}`;
    const rounded = miles < 10 ? miles.toFixed(1) : Math.round(miles);
    return `${rounded} ${rounded === '1.0' ? 'mile' : 'miles'} ${suffix}`;
  }

  /*
   * Place lookup for searches that match no stockist: a UK postcode, postcode
   * area or town becomes a point the list can be sorted from. postcodes.io is
   * free and needs no key. Only searches with no matches are sent to it.
   */
  const PLACES_API = 'https://api.postcodes.io';
  const POSTCODE = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i;
  const OUTCODE = /^[a-z]{1,2}\d[a-z\d]?$/i;
  // When several places share a name, the bigger one wins (Newport the city, not the village)
  const PLACE_RANK = ['City', 'Town', 'Other Settlement', 'Village', 'Suburban Area', 'Hamlet'];
  const placeCache = new Map();

  async function getPlaceResult(path, signal) {
    const response = await fetch(`${PLACES_API}${path}`, { signal });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Place lookup failed (${response.status})`);
    return (await response.json()).result;
  }

  async function findPlace(query, signal) {
    const key = query.replace(/\s+/g, ' ');
    if (placeCache.has(key)) return placeCache.get(key);

    const compact = encodeURIComponent(key.replace(/\s/g, ''));
    let place = null;
    if (POSTCODE.test(key)) {
      const result = await getPlaceResult(`/postcodes/${compact}`, signal);
      if (result) place = { label: result.postcode, lat: result.latitude, lng: result.longitude };
    } else if (OUTCODE.test(key)) {
      const result = await getPlaceResult(`/outcodes/${compact}`, signal);
      if (result) place = { label: result.outcode, lat: result.latitude, lng: result.longitude };
    } else if (key.length >= 3) {
      const results = (await getPlaceResult(`/places?q=${encodeURIComponent(key)}&limit=20`, signal)) || [];
      const rank = (result) =>
        (normalise(result.name_1) === key ? 0 : 10) + (PLACE_RANK.indexOf(result.local_type) + 1 || 9);
      const best = results.sort((a, b) => rank(a) - rank(b))[0];
      if (best) place = { label: best.name_1, lat: best.latitude, lng: best.longitude };
    }

    // Some postcodes (Channel Islands, very new ones) come back without coordinates
    if (place && !(Number.isFinite(place.lat) && Number.isFinite(place.lng))) place = null;
    placeCache.set(key, place);
    return place;
  }

  class StockistFinder extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-stockists-list]');
      this.mapEl = this.querySelector('[data-stockists-map]');
      this.statusEl = this.querySelector('[data-stockists-status]');
      this.emptyEl = this.querySelector('[data-stockists-empty]');
      this.emptyTitle = this.querySelector('[data-stockists-empty-title]');
      this.emptyText = this.querySelector('[data-stockists-empty-text]');
      this.emptyActions = this.querySelector('[data-stockists-empty-actions]');
      this.emptyShop = this.querySelector('.stockists__empty-shop');
      this.allTypesButton = this.querySelector('[data-stockists-all-types]');
      this.nearbyEl = this.querySelector('[data-stockists-nearby]');
      this.nearbyTitle = this.querySelector('[data-stockists-nearby-title]');
      this.searchInput = this.querySelector('[data-stockists-search]');
      this.locateButton = this.querySelector('[data-stockists-locate]');
      this.items = Array.from(this.querySelectorAll('[data-stockist]'));
      this.originalOrder = [...this.items];
      this.itemsByIndex = new Map(this.items.map((item) => [Number(item.dataset.stockist), item]));

      this.query = '';
      this.type = 'all';
      // Distances are measured from a searched place if there is one, otherwise from the visitor
      this.origin = null;
      this.place = null;
      // 'pending' while a place is looked up, 'none' or 'error' when it isn't found
      this.lookup = null;
      this.activeIndex = null;
      this.markers = new Map();

      this.points = this.readPoints();
      this.points.forEach((point) => this.itemsByIndex.get(point.index).setAttribute('data-has-pin', ''));

      this.bindControls();
      this.lazyInitMap();

      this.onBlockSelect = (event) => {
        const item = event.target.closest?.('[data-stockist]');
        if (item && this.contains(item)) this.select(Number(item.dataset.stockist), 'list');
      };
      this.onHashChange = () => this.selectFromHash();
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('hashchange', this.onHashChange);
      this.selectFromHash();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('hashchange', this.onHashChange);
      clearTimeout(this.lookupTimer);
      this.lookupController?.abort();
      this.observer?.disconnect();
      this.map?.remove();
      this.map = null;
    }

    readPoints() {
      let data = [];
      try {
        data = JSON.parse(this.querySelector('[data-stockists-data]')?.textContent || '[]');
      } catch (error) {
        console.error('Stockists: could not read stockist data', error);
      }

      return data
        .map((entry) => ({ ...entry, lat: parseFloat(entry.lat), lng: parseFloat(entry.lng) }))
        .filter(
          (entry) =>
            this.itemsByIndex.has(entry.index) &&
            entry.type !== 'online' &&
            Number.isFinite(entry.lat) &&
            Number.isFinite(entry.lng) &&
            Math.abs(entry.lat) <= 90 &&
            Math.abs(entry.lng) <= 180
        );
    }

    bindControls() {
      let debounce;
      this.searchInput?.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => this.search(normalise(this.searchInput.value)), 150);
      });
      // Enter shouldn't submit anything; jump to the first result instead
      this.searchInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const first = this.items.find((item) => !item.hidden);
        first?.querySelector('[data-stockists-show], a')?.focus();
      });

      this.querySelectorAll('[data-stockists-filter]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) this.setType(radio.value);
        });
      });

      this.querySelectorAll('[data-stockists-clear]').forEach((button) =>
        button.addEventListener('click', () => {
          this.searchInput.value = '';
          this.search('');
          this.searchInput.focus();
        })
      );
      this.allTypesButton?.addEventListener('click', () => this.setType('all'));

      if (this.locateButton && 'geolocation' in navigator && this.points.length) {
        this.locateButton.hidden = false;
        this.locateButton.addEventListener('click', () => this.locate());
      }

      this.list?.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        const item = event.target.closest('[data-has-pin]');
        if (item) this.select(Number(item.dataset.stockist), 'list');
      });
    }

    /* ---------- Filtering ---------- */

    search(query, resort = false) {
      this.query = query;
      clearTimeout(this.lookupTimer);
      this.lookupController?.abort();
      this.lookup = null;
      const hadPlace = Boolean(this.place);
      this.place = null;

      // Nothing on the list mentions it, so it may be a place: look it up and show the nearest instead
      if (query.length >= 2 && this.points.length && !this.items.some((item) => this.textMatches(item))) {
        this.lookup = 'pending';
        this.lookupTimer = setTimeout(() => this.lookUp(query), 250);
      }

      if (hadPlace || resort) this.reorder();
      this.applyFilters();
    }

    async lookUp(query) {
      this.lookupController = new AbortController();
      let place = null;
      let state = null;
      try {
        place = await findPlace(query, this.lookupController.signal);
        if (!place) state = 'none';
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Stockists:', error);
        state = 'error';
      }
      if (query !== this.query) return;

      this.place = place;
      this.lookup = state;
      if (place) this.reorder();
      this.applyFilters();
    }

    setType(value) {
      this.type = value;
      this.querySelectorAll('[data-stockists-filter]').forEach((input) => {
        input.checked = input.value === value;
        input.closest('label').classList.toggle('is-active', input.checked);
      });
      this.applyFilters();
    }

    textMatches(item) {
      // Compare with and without spaces so "eh11" finds "EH1 1AA" style postcodes
      const haystack = item.dataset.search || '';
      return haystack.includes(this.query) || haystack.replace(/\s+/g, '').includes(this.query.replace(/\s+/g, ''));
    }

    // A found place stands in for the search, so every stockist is in range and sorted by distance
    matchesQuery(item) {
      return !this.query || Boolean(this.place) || this.textMatches(item);
    }

    matches(item) {
      const type = item.dataset.type;
      if (this.type === 'in_store' && type === 'online') return false;
      if (this.type === 'online' && type === 'in_store') return false;
      return this.matchesQuery(item);
    }

    applyFilters() {
      let visible = 0;
      this.items.forEach((item) => {
        const show = this.matches(item);
        item.hidden = !show;
        if (show) visible += 1;
      });

      // The empty state takes the list's place, so the map keeps its column
      if (this.list) this.list.hidden = visible === 0;
      this.renderEmpty(visible);

      if (this.nearbyEl) {
        this.nearbyEl.hidden = !this.place;
        if (this.place) this.nearbyTitle.textContent = `No stockists in ${this.place.label}.`;
        // The notice says the same thing, so the status line is kept for screen readers only
        this.statusEl?.classList.toggle('visually-hidden', Boolean(this.place));
      }

      if (this.activeIndex !== null && this.itemsByIndex.get(this.activeIndex)?.hidden) {
        this.clearActive();
        this.map?.closePopup();
      }

      this.syncMarkers();
      this.fitToVisible();
      this.announce(visible);
    }

    renderEmpty(visible) {
      if (!this.emptyEl) return;
      this.emptyEl.hidden = visible > 0;
      if (visible > 0) return;

      const query = this.searchInput.value.trim();
      const pending = this.lookup === 'pending';
      // Matches exist, but the In store / Online filter is hiding them
      const filtered = !pending && this.type !== 'all' && this.items.some((item) => this.matchesQuery(item));

      let title = `Nothing found for “${query}”`;
      let text = 'Try a UK town or postcode, like Leith or EH6, or a shop’s name.';
      if (pending) {
        title = `Looking up “${query}”…`;
        text = '';
      } else if (filtered) {
        title = `No ${this.type === 'online' ? 'online' : 'in-store'} stockists match “${query}”`;
        text = 'There are matches in the full list.';
      } else if (this.lookup === 'error') {
        text = 'Town and postcode search isn’t available right now. Try a shop’s name, or browse the full list.';
      }

      this.emptyEl.classList.toggle('is-loading', pending);
      this.emptyTitle.textContent = title;
      this.emptyText.textContent = text;
      this.emptyText.hidden = !text;
      this.emptyActions.hidden = pending;
      this.emptyShop.hidden = pending || filtered;
      this.allTypesButton.hidden = !filtered;
    }

    announce(visible) {
      if (!this.statusEl) return;
      const query = this.searchInput.value.trim();
      const noun = visible === 1 ? 'stockist' : 'stockists';
      let message = `${visible} ${noun}`;
      if (this.lookup === 'pending') {
        message = `Looking up "${query}"`;
      } else if (this.place) {
        message = `No stockists in ${this.place.label}. ${visible} ${noun}, nearest first`;
      } else {
        if (this.query) message += ` matching "${query}"`;
        if (this.origin && visible) message += ', nearest first';
      }
      this.statusEl.textContent = message;
    }

    /* ---------- Map ---------- */

    lazyInitMap() {
      if (!this.mapEl || !this.points.length) {
        if (this.mapEl) this.setPlaceholder('Locations will appear here once they have map coordinates.');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.initMap();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.initMap();
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this.mapEl);
    }

    initMap() {
      if (!this.mapReady) {
        this.mapReady = loadLeaflet()
          .then((L) => this.buildMap(L))
          .catch((error) => {
            console.error('Stockists:', error);
            this.mapReady = null;
            this.setPlaceholder('The map couldn’t load. Every stockist is in the list.');
          });
      }
      return this.mapReady;
    }

    setPlaceholder(text) {
      const placeholder = this.mapEl?.querySelector('.stockists__map-placeholder');
      if (placeholder) placeholder.textContent = text;
    }

    buildMap(L) {
      if (!this.isConnected || this.map) return;
      this.L = L;
      this.mapEl.querySelector('.stockists__map-placeholder')?.remove();

      this.map = L.map(this.mapEl, { scrollWheelZoom: false }).setView(UK_VIEW.center, UK_VIEW.zoom);

      L.tileLayer(this.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: this.dataset.tileAttribution || '',
      }).addTo(this.map);

      // Don't hijack page scroll: wheel zoom only once someone interacts with the map
      this.map.on('focus click', () => this.map.scrollWheelZoom.enable());
      this.map.on('blur mouseout', () => this.map.scrollWheelZoom.disable());

      this.layer = L.markerClusterGroup({
        maxClusterRadius: 48,
        showCoverageOnHover: false,
        // Shops sharing a postcode fan out at full zoom instead of stacking
        spiderfyOnMaxZoom: true,
        spiderLegPolylineOptions: { weight: 1.5, opacity: 0.7 },
        animate: !REDUCED_MOTION.matches,
        iconCreateFunction: (cluster) => this.clusterIcon(cluster),
      }).addTo(this.map);
      this.points.forEach((point) => this.markers.set(point.index, this.createMarker(point)));

      this.syncMarkers();
      this.fitToVisible(false);

      this.showOrigin();
      if (this.activeIndex !== null) this.openMarker(this.activeIndex, false);
    }

    createMarker(point) {
      const L = this.L;
      const size = point.featured ? [36, 48] : [28, 38];
      const icon = L.divIcon({
        className: `stockists-pin${point.featured ? ' stockists-pin--featured' : ''}`,
        html: `<svg viewBox="0 0 28 38" aria-hidden="true" focusable="false"><path class="stockists-pin__body" d="M14 1.2C7.1 1.2 1.6 6.6 1.6 13.4c0 9.1 12.4 23.3 12.4 23.3s12.4-14.2 12.4-23.3C26.4 6.6 20.9 1.2 14 1.2z"/><circle class="stockists-pin__dot" cx="14" cy="13.4" r="4.4"/></svg>`,
        iconSize: size,
        iconAnchor: [size[0] / 2, size[1]],
        popupAnchor: [0, -size[1] + 6],
      });

      const marker = L.marker([point.lat, point.lng], {
        icon,
        title: point.name,
        alt: point.name,
        keyboard: true,
        riseOnHover: true,
        zIndexOffset: point.featured ? 500 : 0,
        baseZIndex: point.featured ? 500 : 0,
      });

      marker.bindPopup(this.popupHtml(point), { maxWidth: 260, autoPanPadding: [24, 24] });
      // divIcon ignores alt, so name the focusable pin for screen readers
      marker.on('add', () => marker.getElement()?.setAttribute('aria-label', `${point.name}, show details`));
      marker.on('click', () => this.select(point.index, 'map'));
      marker.on('popupclose', () => {
        if (this.activeIndex === point.index) this.clearActive();
      });
      return marker;
    }

    clusterIcon(cluster) {
      const count = cluster.getChildCount();
      const [modifier, size] = count >= 25 ? ['lg', 52] : count >= 10 ? ['md', 44] : ['sm', 36];
      return this.L.divIcon({
        className: `stockists-cluster stockists-cluster--${modifier}`,
        html: `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} stockists. Zoom in to see them</span>`,
        iconSize: [size, size],
      });
    }

    popupHtml(point) {
      const place = [point.town, point.postcode].filter(Boolean).join(', ');
      const directions = `https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lng}`;
      const website = /^https?:\/\//i.test(point.website || '') ? point.website : '';

      return `
        <p class="stockists__popup-name">${escapeHtml(point.name)}</p>
        ${point.address ? `<p class="stockists__popup-meta">${escapeHtml(point.address).replace(/\n/g, '<br>')}</p>` : ''}
        ${place ? `<p class="stockists__popup-meta">${escapeHtml(place)}</p>` : ''}
        <p class="stockists__popup-links">
          <a href="${directions}" target="_blank" rel="noopener">Get directions</a>
          ${website ? `<a href="${escapeHtml(website)}" target="_blank" rel="noopener">Website</a>` : ''}
        </p>`;
    }

    syncMarkers() {
      if (!this.layer) return;
      // Rebuild in one batch: re-clustering pin by pin is slow with 100+ stockists
      const visible = [];
      this.markers.forEach((marker, index) => {
        if (!this.itemsByIndex.get(index)?.hidden) visible.push(marker);
      });
      this.layer.clearLayers();
      this.layer.addLayers(visible);
      if (this.activeIndex !== null) this.highlightPin(this.activeIndex);
    }

    fitToVisible(animate = !REDUCED_MOTION.matches) {
      if (!this.map) return;
      const latLngs = [];
      this.markers.forEach((marker) => {
        if (this.layer.hasLayer(marker)) latLngs.push(marker.getLatLng());
      });

      const from = this.place || this.origin;
      if (from && latLngs.length) {
        // Frame the visitor (or searched place) plus the nearest few stockists, not the whole country
        latLngs.sort((a, b) => this.map.distance(a, from) - this.map.distance(b, from));
        latLngs.splice(3);
        latLngs.push(this.L.latLng(from.lat, from.lng));
      }

      if (!latLngs.length) {
        // Hold still while a place is looked up; otherwise don't leave the map on an area with no pins
        if (this.lookup !== 'pending') this.map.setView(UK_VIEW.center, UK_VIEW.zoom, { animate });
        return;
      }
      if (latLngs.length === 1) {
        this.map.setView(latLngs[0], CLOSE_ZOOM, { animate });
        return;
      }
      this.map.fitBounds(this.L.latLngBounds(latLngs), { padding: [40, 40], maxZoom: CLOSE_ZOOM, animate });
    }

    /* ---------- Selection: list <-> map ---------- */

    select(index, from) {
      const item = this.itemsByIndex.get(index);
      if (!item) return;

      this.setActive(index);

      if (from === 'map') {
        this.scrollListTo(item);
        return;
      }

      if (!DESKTOP.matches && from === 'list') {
        // Map sits above the list on smaller screens
        this.mapEl?.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'center' });
      }

      if (!this.markers.has(index) && !this.map) {
        this.initMap()?.then(() => this.openMarker(index));
        return;
      }
      this.openMarker(index);
    }

    openMarker(index, animate = !REDUCED_MOTION.matches) {
      const marker = this.markers.get(index);
      if (!marker || !this.map) return;
      if (!this.layer.hasLayer(marker)) this.layer.addLayer(marker);
      // Zooms (or fans out a cluster) until the pin is visible, then opens it
      this.layer.zoomToShowLayer(marker, () => {
        if (this.map.getZoom() < CLOSE_ZOOM) {
          this.map.setView(marker.getLatLng(), CLOSE_ZOOM, { animate });
        } else {
          this.map.panTo(marker.getLatLng(), { animate });
        }
        marker.openPopup();
        this.highlightPin(index);
      });
    }

    setActive(index) {
      this.clearActive();
      this.activeIndex = index;
      this.itemsByIndex.get(index)?.classList.add('is-active');
      this.highlightPin(index);
    }

    highlightPin(index) {
      this.markers.forEach((marker, markerIndex) => {
        marker.getElement()?.classList.toggle('is-active', markerIndex === index);
        marker.setZIndexOffset(markerIndex === index ? 1000 : marker.options.baseZIndex);
      });
    }

    clearActive() {
      if (this.activeIndex === null) return;
      this.itemsByIndex.get(this.activeIndex)?.classList.remove('is-active');
      this.markers.get(this.activeIndex)?.getElement()?.classList.remove('is-active');
      this.activeIndex = null;
    }

    scrollListTo(item) {
      // Only scroll the list's own box (desktop). Never jump the page.
      if (!this.list || this.list.scrollHeight <= this.list.clientHeight) return;
      const top = this.list.scrollTop + item.getBoundingClientRect().top - this.list.getBoundingClientRect().top - 12;
      this.list.scrollTo({ top, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    }

    selectFromHash() {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      const item = this.items.find((candidate) => candidate.id === id);
      if (!item) return;
      const index = Number(item.dataset.stockist);
      this.setActive(index);
      if (this.markers.has(index) || this.points.some((point) => point.index === index)) {
        this.initMap()?.then(() => this.openMarker(index, false));
      }
    }

    /* ---------- Use my location ---------- */

    locate() {
      this.locateButton.setAttribute('aria-busy', 'true');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.locateButton.removeAttribute('aria-busy');
          this.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
          // Their own location takes over from any search
          this.searchInput.value = '';
          this.search('', true);
          if (!this.map) this.initMap();
        },
        (error) => {
          this.locateButton.removeAttribute('aria-busy');
          if (this.statusEl) {
            this.statusEl.textContent =
              error.code === error.PERMISSION_DENIED
                ? 'Location is turned off for this site. Search by town or postcode instead.'
                : 'Your location couldn’t be found. Search by town or postcode instead.';
          }
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    // Sorts the list by distance from the searched place or the visitor, or back to its original order
    reorder() {
      const from = this.place || this.origin;
      const distances = new Map(from ? this.points.map((point) => [point.index, milesBetween(from, point)]) : []);
      const suffix = this.place ? `from ${this.place.label}` : 'away';

      this.items.forEach((item) => {
        const miles = distances.get(Number(item.dataset.stockist));
        const label = item.querySelector('[data-stockists-distance]');
        if (label) label.textContent = miles === undefined ? '' : formatMiles(miles, suffix);
      });

      // Stockists with a location first, nearest to furthest, then online-only in their original order
      const sorted = [...this.originalOrder].sort((a, b) => {
        const da = distances.get(Number(a.dataset.stockist));
        const db = distances.get(Number(b.dataset.stockist));
        if (da === undefined && db === undefined) return 0;
        if (da === undefined) return 1;
        if (db === undefined) return -1;
        return da - db;
      });
      this.list.append(...sorted);
      this.items = sorted;
      if (this.list.scrollTo) this.list.scrollTo({ top: 0 });
      this.showOrigin();
    }

    showOrigin() {
      if (!this.map) return;
      const from = this.place || this.origin;
      if (!from) {
        this.originMarker?.remove();
        this.originMarker = null;
        return;
      }
      const L = this.L;
      if (this.originMarker) {
        this.originMarker.setLatLng([from.lat, from.lng]);
        return;
      }
      this.originMarker = L.marker([from.lat, from.lng], {
        icon: L.divIcon({ className: 'stockists-you', iconSize: [14, 14] }),
        keyboard: false,
        interactive: false,
        zIndexOffset: 2000,
      }).addTo(this.map);
    }
  }

  customElements.define('stockist-finder', StockistFinder);
}
